// Currency-aware money input. The value/onChange contract is a CANONICAL decimal
// STRING (e.g. "5000000" or "5000000.50") — never a JS number and never a grouped
// string like "5,000,000" — so it drops straight into a decimal-safe API payload
// with no float rounding. The field shows grouped digits + a currency prefix while
// the user types; grouping/fraction rules come from the currency (see money.ts).
//
// Rules enforced: digits + at most one decimal point; fraction digits clamped to
// the currency's precision (0 for TZS/KES/RWF/UGX, 2 for USD); leading zeros
// collapsed; negatives rejected. Emits '' when cleared.
import React, { useMemo } from 'react';
import { fractionDigitsFor } from '@/lib/money';

function groupInt(intDigits: string): string {
  // Group in threes via regex — no Number() so we never touch float precision.
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Turn arbitrary keyboard/paste input into { canonical, display } under the
// currency's precision. canonical has no grouping; display is grouped.
export function sanitizeMoney(input: string, dp: number): { canonical: string; display: string } {
  let s = (input || '').replace(/[^\d.]/g, ''); // strip everything but digits and dots (drops '-')
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    // collapse any further dots
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  // For whole-unit currencies, drop the fractional part entirely (don't concat it).
  if (dp === 0 && firstDot !== -1) s = s.slice(0, firstDot);
  const hasDot = dp > 0 && s.includes('.');
  let [intPart = '', fracPart = ''] = s.split('.');
  intPart = intPart.replace(/^0+(?=\d)/, ''); // collapse leading zeros, keep a lone "0"
  if (fracPart.length > dp) fracPart = fracPart.slice(0, dp);

  const canonical = hasDot ? `${intPart || '0'}.${fracPart}` : intPart;
  const groupedInt = intPart ? groupInt(intPart) : hasDot ? '0' : '';
  const display = hasDot ? `${groupedInt}.${fracPart}` : groupedInt;
  return { canonical, display };
}

export const MoneyInput: React.FC<{
  value: string;                 // canonical decimal string
  onChange: (canonical: string) => void;
  currency?: string;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}> = ({ value, onChange, currency = 'TZS', id, disabled, placeholder, className, ...aria }) => {
  const dp = fractionDigitsFor(currency);
  const display = useMemo(() => sanitizeMoney(value ?? '', dp).display, [value, dp]);

  return (
    <div className={`flex ${className || ''}`}>
      <span
        aria-hidden="true"
        className="inline-flex items-center rounded-l-lg border border-r-0 border-border bg-muted px-3 text-sm text-muted-foreground"
      >
        {currency}
      </span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        disabled={disabled}
        placeholder={placeholder ?? (dp === 0 ? '0' : '0.00')}
        onChange={(e) => onChange(sanitizeMoney(e.target.value, dp).canonical)}
        className="flex-1 min-w-0 rounded-r-lg border border-border bg-background text-foreground text-sm px-3 py-2 text-right tabular-nums outline-none focus:border-orange-400 disabled:bg-muted disabled:text-muted-foreground"
        {...aria}
      />
    </div>
  );
};

export default MoneyInput;
